import { useState } from "react";
import { cloturerDebats, rouvrirDebats } from "@/lib/apiProcedure";
import type { PhaseProcedurale } from "@/types/procedure";

interface Props {
  caseId: string | number;
  phase: PhaseProcedurale;
  onRefresh: () => void;
}

/**
 * Clôture / réouverture des débats (E5) — acte juridictionnel.
 * Affichage piloté par la phase serveur ; à intégrer dans le cockpit arbitre
 * ou le détail du dossier.
 */
export function ClotureDebatsButton({ caseId, phase, onRefresh }: Props) {
  const [loading, setLoading] = useState(false);
  const [showReopen, setShowReopen] = useState(false);
  const [motif, setMotif] = useState("");

  const handleCloturer = async () => {
    if (!confirm("Clôturer les débats ? Les dépôts seront gelés.")) return;
    setLoading(true);
    try {
      await cloturerDebats(caseId);
      onRefresh();
    } finally {
      setLoading(false);
    }
  };

  const handleRouvrir = async (e: React.FormEvent) => {
    e.preventDefault();
    if (motif.trim().length < 10) {
      alert("Le motif doit faire au moins 10 caractères.");
      return;
    }
    setLoading(true);
    try {
      await rouvrirDebats(caseId, motif);
      setShowReopen(false);
      onRefresh();
    } finally {
      setLoading(false);
    }
  };

  if (phase === "INSTRUCTION_CONTRADICTOIRE") {
    return (
      <button
        onClick={handleCloturer}
        disabled={loading}
        className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700 disabled:opacity-50"
      >
        {loading ? "Clôture…" : "Clôturer les débats (E5)"}
      </button>
    );
  }

  if (phase === "DEBATS_CLOS") {
    return (
      <div className="space-y-2">
        <span className="text-sm font-bold text-red-600">
          🛑 Débats clos (dépôts gelés)
        </span>
        <button
          onClick={() => setShowReopen((v) => !v)}
          className="block text-xs text-blue-500 underline"
        >
          Réouvrir exceptionnellement
        </button>
        {showReopen && (
          <form
            onSubmit={handleRouvrir}
            className="mt-2 p-3 border rounded bg-yellow-50 space-y-2"
          >
            <textarea
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              placeholder="Motif impératif de réouverture (≥ 10 caractères)…"
              className="w-full p-2 border rounded text-sm"
            />
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={loading}
                className="bg-yellow-600 text-white px-3 py-1 rounded text-sm disabled:opacity-50"
              >
                Confirmer la réouverture
              </button>
              <button
                type="button"
                onClick={() => setShowReopen(false)}
                className="text-gray-500 text-sm"
              >
                Annuler
              </button>
            </div>
          </form>
        )}
      </div>
    );
  }

  return null;
}

/**
 * Bandeau à afficher dans le formulaire de dépôt de conclusion : si les débats
 * sont clos, le dépôt est interdit (le backend renvoie 409 de toute façon).
 */
export function GelDepotGuard({ phase }: { phase: PhaseProcedurale }) {
  if (
    phase !== "DEBATS_CLOS" &&
    phase !== "EN_DELIBERE" &&
    phase !== "SENTENCE_RENDUE"
  ) {
    return null;
  }
  return (
    <div
      className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4"
      role="alert"
    >
      <strong className="font-bold">Dépôt interdit. </strong>
      <span>
        Les débats ont été clôturés (phase {phase}). Aucune nouvelle conclusion
        ne peut être déposée, sauf réouverture explicite par le tribunal.
      </span>
    </div>
  );
}
